/**
 * Persian label catalogue.
 *
 * Every contract enum gets a Persian label here, and a test asserts the maps
 * cover the enum exhaustively (`src/api/contract.test.ts`) — so adding an action
 * or a problem code on the server cannot leave the console silent.
 */

import type {
  ActionKind,
  ActivationStatusValue,
  ActivationStepName,
  AlarmSeverity,
  CampaignAction,
  CampaignKind,
  CampaignStatus,
  CampaignWindowStatus,
  DiagnosticsType,
  EventTypeKind,
  JobStatus,
  MaintenanceScope,
  MaintenanceState,
  OpticalWindow,
  ProblemCode,
  IncidentState,
  IncidentTimelineKind,
} from '../api/types';
import {
  ACTION_KINDS,
  ACTIVATION_STATUSES,
  ACTIVATION_STEP_NAMES,
  ALARM_SEVERITIES,
  CAMPAIGN_ACTIONS,
  CAMPAIGN_KINDS,
  CAMPAIGN_STATUSES,
  CAMPAIGN_WINDOW_STATUSES,
  DIAGNOSTICS_TYPES,
  JOB_STATUSES,
  OPTICAL_WINDOWS,
  PROBLEM_CODES,
} from '../api/types';

/** Stable, domain codes are shown *and* translated: the code is the contract. */
export const PROBLEM_LABELS: Record<ProblemCode, string> = {
  'acs-unreachable': 'دسترس‌ناپذیری ACS/NBI',
  'device-not-found': 'دستگاه یافت نشد',
  'device-offline': 'دستگاه آفلاین است',
  'binding-conflict': 'تعارض بندینگ',
  'action-unsupported': 'عملیات پشتیبانی نمی‌شود',
  'policy-conflict': 'تعارض سیاست',
  'job-timeout': 'اتمام زمان کار',
  'job-not-found': 'کار یافت نشد',
  'contract-version-unsupported': 'نسخهٔ قرارداد نامعتبر',
  'stale-snapshot': 'اسنپ‌شات کهنه',
  'reference-invalid': 'ارجاع نامعتبر',
  'scope-insufficient': 'اسکوپ ناکافی',
  'rate-limited': 'سقف نرخ درخواست',
  'idempotency-conflict': 'تعارض Idempotency-Key',
  // U-03-a (plan ruling D3): «this key is already carrying your request, and its effect is running
  // now» is not the sentence above. A caller who reads it as a conflict goes looking for a body
  // difference that does not exist — the two requests are byte-identical here.
  'idempotency-in-progress': 'درخواستی با این Idempotency-Key هنوز در حال اجرا است',
  'signature-invalid': 'امضای اعتبارسنجه نامعتبر',
  'olt-not-found': 'OLT یافت نشد',
  'adapter-unreachable': 'دسترس‌ناپذیری Adapter',
  // U-05/C1d (AUD-05/G-OLT-13). The adapter is up and refusing: the next step is a
  // configuration, not a service. The second one is a write that did not happen, so the
  // label has to say «retry» rather than «fix the deployment».
  'adapter-unconfigured': 'پیوند OLT Adapter پیکربندی نشده است',
  'adapter-state-unavailable': 'Adapter نتوانست state خود را ذخیره کند',
  // C9: the adapter answered and its answer does not match the request it was given —
  // a different fact from both lines above, and the label says so («پاسخ» not «پیوند»).
  'adapter-answer-invalid': 'پاسخ Adapter با درخواست نخواند',
  // G-PRV-05: the NBI answered and refused the preset document — «رسیدن‌ناپذیر» نیست.
  'acs-refused': 'NBI سند را نپذیرفت',
  'firmware-not-found': 'تصویر فیرمویر یافت نشد',
  'campaign-not-found': 'کمپین یافت نشد',
  'alarm-not-found': 'آلارم یافت نشد',
  'maintenance-not-found': 'پنجرهٔ نگهداشت یافت نشد',
  'incident-not-found': 'حادثه یافت نشد',
  'incident-already-closed': 'این حادثه پیش‌تر بسته شده است',
  'alarm-already-in-incident': 'این آلارم در یک حادثهٔ باز دیگر است',
  'notification-channel-not-found': 'کانال اطلاع‌رسانی یافت نشد',
  'channel-kind-unsupported': 'این نوع کانال ساخته نشده است',
  'notification-url-invalid': 'نشانی کانال باید http(s) باشد',
  'report-not-found': 'گزارش یافت نشد',
  'capability-unproven': 'قابلیت تأیید نشده است',
  'operation-not-found': 'عملیات یافت نشد',
  'confirmation-required': 'تأییدِ تایپ‌شده لازم است',
  'manifest-not-found': 'manifest یافت نشد',
  'not-an-action': 'این slot پارامتر است، نه RPC',
  'job-not-cancellable': 'این کار قابل لغو نیست',
  'checksum-mismatch': 'checksum با بایت‌های رسیده نمی‌خواند',
  // Circuit H's admin doors (H7-b, H8-b). The console renders whatever a call answers with, and
  // `Record<ProblemCode, string>` is total: a code the contract can answer with and this table
  // does not name is a banner reading «unknown problem code» where the server said «run
  // --baseline first».
  'event-not-found': 'رویداد صف تحویل یافت نشد',
  'event-not-redrivable': 'این رویداد dead letter نیست',
  'migration-unrecorded': 'نسخهٔ schema در این مخزن ثبت نشده است',
  'migration-ahead': 'schema مخزن جلوتر از این build است',
  // DA-38: هر دو پاسِ single-flight — «که برنده شد» نه «build اشتباه است».
  'migration-busy': 'مهاجر دیگری در حال اجرای مهاجرت است',
  'migration-lost-race': 'مهاجر دیگری رکورد schema را جلو برده است',
  'backup-not-found': 'بک‌اپ یافت نشد',
  'backup-dir-unavailable': 'دایرکتوری بک‌اپ در دسترس نیست',
  // U-17: a hold is a freeze on a retention family — «یافت نشد» is the lookup miss, and the
  // lift's own sentence says the recorded lifting stands, so an operator does not retry it.
  'legal-hold-not-found': 'قفل حقوقی یافت نشد',
  'legal-hold-already-lifted': 'قفل حقوقی پیش‌تر برداشته شده است',
  // R1's identity doors. `auth-unconfigured` is the one an operator meets on a fresh deployment: the
  // label says what is missing (an active owner) rather than what failed, because the answer is a
  // deploy step and not a different password.
  'auth-unconfigured': 'هیچ مالک فعالی روی این استقرار نیست',
  'user-not-found': 'کاربر کنسول یافت نشد',
  'user-exists': 'این نام کاربری قبلاً گرفته شده است',
  'last-owner': 'آخرین مالک فعال را نمی‌توان تنزل داد یا حذف کرد',
  // U-06 (`REQ-PROFILE`). Three words for three different facts, because the operator's next step is
  // a different one in each: fix the document (its report carries every line), re-read the profile
  // and diff it, or change what the deployment declares about itself — not go and find a permission.
  'profile-invalid': 'سند پروفایل از اعتبارسنجی عبور نکرد',
  'profile-stale-version': 'نسخهٔ فعال پروفایل عوض شده است',
  'profile-release-blocked': 'فعال‌سازی این پروفایل در این استقرار مسدود است',
  // U-06-d: the rollback door addresses a history row by number. «یافت نشد» here is about the
  // deployment's own ledger, not about the operator's request, and the hint has to say why.
  'profile-version-not-found': 'نسخهٔ پروفایل در تاریخچهٔ این store یافت نشد',
  // N1/N2 (G-ALM-03/06): the alarm lifecycle's five refusals, one label each — the console has to
  // say WHICH fact it is, because the operator's next step differs: a closure that stands (read
  // the record), a closed record that moves no more (read the record), or a caller that is not a
  // person (log in as an operator).
  'alarm-already-cleared': 'این آلارم پیش‌تر بسته شده است',
  'alarm-not-escalatable': 'آلارمِ بسته‌شده قابل ارتقا نیست',
  'alarm-not-assignable': 'آلارمِ بسته‌شده قابل واگذاری نیست',
  'alarm-not-ackable': 'آلارمِ بسته‌شده قابل تأیید نیست',
  'actor-not-human': 'این در فقط برای یک کاربرِ انسانی است',
  // S1 (G-SUB-01/03) — the subscriber surface's four words. The label has to name the NEXT STEP,
  // because the four answers point at four different places: a deploy setting (the feature is off),
  // a wrong id (two separate lookups, one per entity), and a taken name.
  'subscriber-disabled': 'این استقرار سطح مشترک را اجرا نمی‌کند',
  'subscriber-not-found': 'مشترکی با این شناسه یافت نشد',
  'service-profile-not-found': 'پروفایل سرویسی با این شناسه یافت نشد',
  'service-profile-conflict': 'پروفایل سرویسی با این نام از قبل وجود دارد',
  // S4 (G-SUB-05) — the lifecycle step's own refusal. The label has to say that the ROW
  // is in another state, not that the request was wrong: the row exists, the request had
  // no body at all, and nothing was written.
  'subscriber-state-conflict': 'وضعیتِ مشترک با این گذار نمی‌خواند',
};

/** Operator-facing hint per code (what to do next, not what broke). */
export const PROBLEM_HINTS: Partial<Record<ProblemCode, string>> = {
  'device-offline':
    'دستگاه در لحظهٔ درخواست پاسخ نداده است؛ فرمان به‌صورت fail-closed رد شد. پس از inform بعدی دوباره تلاش کنید.',
  'rate-limited':
    'بودجهٔ connection-request هر دقیقه پر شده است؛ پس از مهلت ذکرشده دوباره تلاش کنید.',
  'idempotency-conflict':
    'این کلید قبلاً با بدنهٔ دیگری ثبت شده است. یک کلید جدید بگیرید یا دقیقاً همان بدنه را بفرستید.',
  // U-03-a: the next step here is a *wait*, not an edit — the effect of this exact request is
  // already running, and resending it now is the duplicate the refusal exists to stop. The lease
  // (`ACS_IDEMPOTENCY_CLAIM_LEASE_SECONDS`) bounds how long the answer can stay true.
  'idempotency-in-progress':
    'همین درخواست با همین کلید هنوز در حال اجراست؛ منتظر بمانید و سپس دوباره بفرستید. اگر پاسخی دریافت نکردید، پس از مهلت lease همان کلید را دوباره امتحان کنید.',
  'scope-insufficient':
    'اعتبارنامهٔ جلسه اسکوپ این عملیات را ندارد. با نقش اپراتور/ادمین دوباره وارد شوید.',
  'signature-invalid':
    'اعتبارنامه (client assertion) رد شد — نشانهٔ ورود منقضی‌شده یا کلید ثبت‌نشده.',
  'stale-snapshot': 'مقادیر خواسته‌شده قدیمی‌تر از حد مجازاند؛ read_snapshot بزنید.',
  'action-unsupported': 'این عمل روی این مدل/برند ممکن نیست (به capability دستگاه نگاه کنید).',
  'adapter-unreachable': 'سرویس OLT Adapter پاسخ نداد؛ واحد عیب‌یابی: olt-adapter (پورت ۸۰۹۳).',
  // U-05/C1d: two different next steps behind two 503s. The first one is a configuration
  // the deployment never got (the shared bearer between the façade and the adapter), and
  // the second one is a write the adapter could not persist — which the adapter's own
  // contract says is idempotent, so the second one's next step is a retry.
  'adapter-unconfigured':
    'پیوند بین façade و OLT Adapter پیکربندی نشده است (توکنِ مشترک در دو سوی پیوند). ' +
    'واحد عیب‌یابی: پیکربندیِ استقرار.',
  'adapter-state-unavailable':
    'Adapter درخواست را گرفت ولی نتوانست state را ذخیره کند؛ عملیات idempotent است و ' +
    'می‌توان دوباره فرستاد.',
  // C9: the adapter is UP and replied — what it said does not match the request it was
  // given (a bulk run that reports a different number of outcomes than it had targets).
  // Nothing about the deployment is broken, so this is not a «fix the deployment» fact;
  // the next step is a version of the adapter, and the key is handed back so a corrected
  // request can be sent.
  'adapter-answer-invalid':
    'پاسخِ OLT Adapter با درخواستی که فرستادید نمی‌خواند (تعداد نتایج با تعداد هدف‌ها ' +
    'یکی نیست). سرویس بالاست ولی نسخه‌اش با این در نمی‌خواند؛ کلیدِ Idempotency-Key ' +
    'پس داده شده تا درخواستِ اصلاح‌شده را بفرستید.',
  // G-PRV-05: the NBI answered this preset write with an error — the document was
  // refused, not the link lost; the key is handed back so a corrected document can
  // be sent under the same Idempotency-Key.
  'acs-refused':
    'GenieACS نوشتنِ preset را با خطا پاسخ داد (نه اینکه در دسترس نباشد). سندِ ' +
    'فرستاده‌شده را اصلاح کنید؛ کلیدِ Idempotency-Key پس داده شده تا همان کلید ' +
    'درخواستِ اصلاح‌شده را ببرد.',
  'firmware-not-found': 'file_id در انبار ایمیج‌ها وجود ندارد؛ فهرست /ops/v1/firmware را ببینید.',
  'campaign-not-found': 'کمپین حذف یا بازشمارده شده است؛ /ops/v1/campaigns را دوباره بخوانید.',
  'alarm-not-found':
    'این شناسه در outbox نیست یا از گونهٔ غیرآلارمی اشتقاق شده است؛ فید را دوباره بخوانید ' +
    '(alarm_id همان شناسهٔ رویداد CloudEvents است).',
  'maintenance-not-found':
    'این شناسه پنجرهٔ نگهداشتی نیست که این استقرار بشناسد؛ فهرست /ops/v1/maintenance را ' +
    'دوباره بخوانید (پنجره‌ها حذف نمی‌شوند — لغوشده هم در فهرست می‌ماند).',
  'incident-not-found':
    'این شناسه حادثه‌ای نیست که این استقرار بشناسد؛ فهرست /ops/v1/incidents را دوباره بخوانید.',
  'incident-already-closed':
    'حادثه بسته شده است و بستنش تکرار نمی‌شود؛ سطر خودش می‌گوید چه کسی و کِی بست.',
  'alarm-already-in-incident':
    'این آلارم از قبل در یک حادثهٔ بازِ دیگر است؛ درمانش همان حادثه است، نه ساختن حادثهٔ دوم.',
  'notification-channel-not-found':
    'این شناسه کانالِ اطلاع‌رسانی نیست که این استقرار بشناسد؛ فهرست /ops/v1/notification-channels را دوباره بخوانید.',
  'channel-kind-unsupported':
    'فقط webhook ساخته شده است؛ پنلِ SMS و باتِ Telegram حکمِ مالک را می‌خواهند — تا آن روز هیچ فرودگاهی اختراع نشده.',
  'notification-url-invalid': 'نشانی کانال باید یک URL با پروتکل http یا https باشد.',
  // Circuit M / T doors. Each hint names the read that answers the question, because these four
  // codes are all «go look at something» rather than «retry».
  'capability-unproven':
    'کمبودِ اجازه نیست: برای این مدل شاهدِ اندازه‌گیری‌شده‌ای ثبت نشده (§۴.۴). فهرست ' +
    '`/ops/v1/devices/{id}/operations` دلیل قفل هر عملیات را نشان می‌دهد.',
  'operation-not-found':
    'این نام در کاتالوگ عملیات‌های نشرشده نیست (§۴.۳)؛ همان فهرستِ عملیات‌ها را بخوانید.',
  'confirmation-required':
    'درِ پارامتر خام از اپراتور می‌خواهد متنِ تأیید را عیناً تایپ کند (§۶.۱)؛ رشتهٔ دقیق در ' +
    'بدنهٔ همین پاسخ آمده است — بدنهٔ درخواست درست بود، فقط این امضا کم بود.',
  'manifest-not-found':
    'هیچ سندِ manifestِ بارگذاری‌شده‌ای این model و slot را نام نمی‌برد (§۴.۸)؛ ' +
    '`/ops/v1/manifests/{model}` شواهد همان مدل را نشان می‌دهد.',
  'not-an-action':
    'این slot مقدار دارد و پارامتر است، پس روشنی برای :run وجود ندارد (§۴.۱۰)؛ نوشتنش درِ ' +
    'عملیات با مقدار است.',
  'job-not-cancellable':
    'کار نتیجه‌اش را پس داده است (applied / timeout / failed) و لغو فقط انتظارِ تمام‌نشده را ' +
    'می‌بندد (§۴.۱۱)؛ سوابق بازنویسی نمی‌شوند.',
  // E2: the registry measures. This is the answer that says «the file you sent is not the file you
  // named» — the digest of both sides is in the detail, so the operator compares the two files instead
  // of guessing, and the Idempotency-Key is still free because a rejection was never recorded.
  'checksum-mismatch':
    'فایلِ ارسالی با `checksum_sha256` اعلام‌شده برابر نیست؛ هیچ رکوردی نوشته نشد و کلید idempotency ' +
    'هم آزاد ماند. هر دو digest در detail است: فایل مبدأ را دوباره hash کنید (یا دوباره دانلودش کنید) ' +
    'و همان کلید را دوباره بفرستید.',
  // U-06's two «what do I do now» answers. `profile-invalid` deliberately has no hint: its whole body
  // IS the instruction list, and a hint that says «fix the document» beside eleven fixable lines adds
  // nothing but a second place to say it.
  'profile-stale-version': 'نسخه‌ای که دیدید دیگر نسخهٔ فعال نیست؛ دوباره بخوانید و diff بگیرید.',
  'profile-release-blocked':
    'این استقرار امروز این بدنه را فعال نمی‌کند. دو حالت دارد و راه‌حلشان یکی نیست: ' +
    'در «:validate» و «PUT» تنها ایرادهای سند قواعد release خودِ استقرارند (mock در production یا ' +
    'ارجاع اسرار کامل نیست) — پس چیزی که باید عوض شود ادعای استقرار است نه سند؛ ' +
    'و در «:rollback» نسخه‌ای از تاریخچه با قواعد امروزِ این build نمی‌خواند — پس بدنه دست شما نیست ' +
    'و باید از نو با «PUT» اعلام شود.',
  // U-06-d: the two histories that produce this 404 are not separable from the door's point of view —
  // «this version number was never minted» and «it was trimmed by the bounded window» — so the hint
  // sends the operator to the one place that can answer: the version list.
  'profile-version-not-found':
    'این شمارهٔ نسخه در تاریخچهٔ store نیست (هرگز فعال نشده، یا از پنجرهٔ محدودِ تاریخچه بیرون ' +
    'رفته است). از `GET /ops/v1/operator-profile/versions` ببینید کدام نسخه‌ها هنوز این‌جا هستند؛ ' +
    'بدنهٔ یک نسخهٔ قدیمی از این در بیرون نمی‌آید.',
  // N1/N2 (G-ALM-03/06): the lifecycle's five hints — each one names the read that answers the
  // question, because these refusals are all «go and look at the record», never «retry».
  'alarm-already-cleared':
    'بسته‌شدنِ این آلارم پابرجاست و تکرار نمی‌شود. نامِ بسته‌کننده در رکوردِ آلارم و در دفترِ ' +
    'audit آمده است؛ اگر بسته‌شدن اشتباه بود، آلارمِ تازه‌ای از رویدادِ زنده بیاید.',
  'alarm-not-escalatable':
    'رکوردِ بسته‌شده تغییر نمی‌کند. وضعیتِ فعلیِ این آلارم را در فیدِ آلارم‌ها ببینید.',
  'alarm-not-assignable':
    'رکوردِ بسته‌شده تغییر نمی‌کند. وضعیتِ فعلیِ این آلارم را در فیدِ آلارم‌ها ببینید.',
  'alarm-not-ackable':
    'رکوردِ بسته‌شده تغییر نمی‌کند. وضعیتِ فعلیِ این آلارم را در فیدِ آلارم‌ها ببینید.',
  'actor-not-human':
    'این در نامِ یک اپراتور را ثبت می‌کند و assertionِ ماشین نمی‌تواند جای آدم بنشیند. با نشستِ ' +
    'کنسول و نقشِ اپراتور یا ادمین وارد شوید.',
};

export const ACTION_LABELS: Record<ActionKind, string> = {
  apply_policy: 'اعمال سیاست',
  read_snapshot: 'خواندن snapshot',
  reboot: 'ری‌استارت',
  factory_reset: 'بازگشت به کارخانه',
  download: 'دانلود فیرمویر',
  rotate_credential: 'تغییر رمز',
};

export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  queued: 'در صف',
  running: 'در حال اجرا',
  completed: 'موفق',
  failed: 'ناموفق',
};

export const ACTIVATION_STATUS_LABELS: Record<ActivationStatusValue, string> = {
  await_inform: 'در انتظار inform',
  informed: 'inform شد',
  bound: 'بند شد',
  provisioned: 'پیکربندی شد',
  failed: 'ناموفق',
};

/**
 * The three OLT-side steps a ZTP activation runs, plus `deauthorize` (only on
 * device replace). Wire names come from `ACTIVATION_STEP_NAMES`.
 */
export const ACTIVATION_STEP_LABELS: Record<ActivationStepName, string> = {
  authorize: 'مجوز ONU روی OLT',
  'assign-profile': 'اختصاص پروفایل خط/سرویس',
  'push-tr069': 'ارسال URL و بازهٔ inform TR-069',
  deauthorize: 'لغو مجوز ONU قبلی',
};

/** `DiagnosticsRequest.type` — the three active probes of plan P8. */
export const DIAGNOSTICS_TYPE_LABELS: Record<DiagnosticsType, string> = {
  ping: 'پینگ',
  traceroute: 'traceroute',
  speedtest: 'speedtest (TR-143)',
};

/** What `speedtest` needs on the device doc — the API's own gate. */
export const TR143_CAPABILITY = 'tr143';

export const EVENT_KIND_LABELS: Record<EventTypeKind, string> = {
  'device.discovered': 'کشف دستگاه',
  'device.bound': 'بند شدن',
  'device.inform': 'inform',
  'device.online_status': 'وضعیت اتصال',
  'device.parameter.changed': 'تغییر پارامتر',
  'device.command.completed': 'اتمام فرمان',
  'device.config.drift': 'انحراف پیکربندی',
  'device.firmware.updated': 'بروز فیرمویر',
  'device.factory_reset': 'بازگشت کارخانه',
  'onu.authorized': 'مجوز ONU روی OLT',
  'onu.los': 'قطع نوری (LOS)',
  'optical.degraded': 'افت توان نوری',
  'diagnostics.completed': 'اتمام عیب‌یابی',
  'provision.completed': 'اتمام پیکربندی سرویس',
  'campaign.progress': 'پیشرفت کمپین',
  'reconciliation.mismatch': 'مغایرت reconciliation',
  'device.replaced': 'تعویض ONU',
};

/**
 * `Alarm.severity` — the three members of the contract enum, and only these.
 * The server maps event kind → severity in one fixed table, so the console never
 * has to translate a trap vocabulary here (`severityTone()` in `lib/status.ts`
 * stays for the free-text `data.severity` of a trap payload).
 */
export const ALARM_SEVERITY_LABELS: Record<AlarmSeverity, string> = {
  critical: 'بحرانی',
  warning: 'هشدار',
  info: 'اطلاعی',
};

/**
 * ALM-05 — the two-word alphabets of a maintenance window
 * (`models.py:558`/`:565`). The state has deliberately no third word:
 * «گذشته» would be the browser minting a second truth for a row the server
 * still calls `active`.
 */
export const MAINTENANCE_SCOPE_LABELS: Record<MaintenanceScope, string> = {
  device: 'دستگاه',
  olt: 'OLT',
};

export const MAINTENANCE_STATE_LABELS: Record<MaintenanceState, string> = {
  active: 'فعال',
  cancelled: 'لغو‌شده',
};

/** N5 — the incident lifecycle's two words, and how the timeline entries read. */
export const INCIDENT_STATE_LABELS: Record<IncidentState, string> = {
  open: 'باز',
  closed: 'بسته',
};

export const INCIDENT_TIMELINE_LABELS: Record<IncidentTimelineKind, string> = {
  created: 'گشودن',
  note: 'یادداشت',
  attach: 'پیوستِ آلارم',
  close: 'بستن',
};

/** `data.online` of an offline `device.online_status` row. */
export const ALARM_ACK_LABELS = {
  open: 'باز',
  acked: 'تأیید شده',
  // N1/N2: the third state of the lifecycle — a CLOSED record (the `:clear`
  // door). It is terminal: nothing moves a closed alarm any more.
  cleared: 'بسته شده',
} as const;

/**
 * ONU row vocabulary of the adapter drivers (`olt_adapter/drivers/base.py`).
 * Free text on the wire — an unknown value is still rendered, with the wire
 * string next to the Persian guess.
 */
export const ONU_STATUS_LABELS: Record<string, string> = {
  registered: 'ثبت‌شده روی OLT',
  unregistered: 'ثبت‌نشده/خاموش',
};

export const ONU_AUTH_STATE_LABELS: Record<string, string> = {
  authorized: 'مجوز دارد',
  unauthorized: 'بدون مجوز',
};

/** `window` of `GET …/optical` — a read slot, not a chart range yet. */
export const OPTICAL_WINDOW_LABELS: Record<OpticalWindow, string> = {
  '1h': 'یک ساعت اخیر',
  '24h': 'بیست‌وچهار ساعت اخیر',
  '7d': 'هفت روز اخیر',
  '30d': 'سی روز اخیر',
};

/** Persian label for any wire ONU status; unknown values stay literal. */
export function onuStatusLabel(status: string | null | undefined): string | null {
  if (!status) return null;
  return ONU_STATUS_LABELS[status] ?? null;
}

export function onuAuthStateLabel(state: string | null | undefined): string | null {
  if (!state) return null;
  return ONU_AUTH_STATE_LABELS[state] ?? null;
}

/**
 * Adapter error codes recorded on a failed activation step
 * (`ActivationStatus.error.code`). These are the OLT-adapter's own vocabulary,
 * mapped to façade problem codes by `_ADAPTER_PROBLEM_MAP` in
 * `acs_service/routers/ops.py` — they are NOT members of the contract
 * `Problem.code` enum (`onu-not-found` and `driver-unsupported` have no code of
 * their own), which is why this table is keyed by a free string and every entry
 * also names the code it came from.
 */
export const ADAPTER_ERROR_LABELS: Record<string, string> = {
  'olt-not-found': 'OLT در inventory adapter ثبت نشده است',
  'onu-not-found': 'ONU روی آن OLT یافت نشد',
  'driver-unsupported': 'درایور این OLT آن عمل را پشتیبانی نمی‌کند',
  'adapter-unreachable': 'سرویس OLT Adapter پاسخ نداد',
  'acs-unreachable': 'ACS از سمت adapter در دسترس نیست',
  // U-05/C1d (AUD-05/G-OLT-13): the adapter's own three codes, recorded verbatim on a failed
  // activation step. They are the adapter's vocabulary, not the contract's — the façade maps
  // them onto `adapter-unconfigured` / `adapter-state-unavailable` before they reach a caller —
  // but the step record keeps the adapter's own word, so this table has to know it.
  'adapter-unauthorized': 'توکن bearer پذیرفته نشد',
  'adapter-unconfigured': 'توکنی برای OLT Adapter پیکربندی نشده است',
  'state-write-failed': 'Adapter نتوانست state را ذخیره کند',
};

export function adapterErrorLabel(code: string): string {
  return ADAPTER_ERROR_LABELS[code] ?? code;
}

/**
 * The four roles of `docs/RBAC_AND_CONSOLE_FA.md` §۲ — the vocabulary the server actually
 * publishes in `EffectivePermissions.roles`.
 *
 * The three-name table this family used to carry (`بیننده`/`اپراتور`/`ادمین`) is gone with U-08-f
 * part 2: D10 replaced the console's role field with a capability, so a label for a role the server
 * does not publish had nothing left to label. This table is a *label* table only: the badge shows a
 * role it was told, and `rbacRoleLabel` falls back to the wire word rather than inventing a Persian
 * name for a role §۲ does not list.
 */
export const RBAC_ROLE_LABELS = {
  owner: 'مالک',
  admin: 'مدیر',
  operator: 'اپراتور',
  user: 'کاربر',
} as const;

export function rbacRoleLabel(role: string | null | undefined): string {
  if (!role) return 'بدون نقش';
  return RBAC_ROLE_LABELS[role as keyof typeof RBAC_ROLE_LABELS] ?? role;
}

/**
 * The four answers `useCapability()` can give (U-08-f/D10) — the wire words `read`/`edit`, the
 * server's absence (`none`) and the console's own admission that the mirror has not arrived
 * (`unknown`). Labels only: this table never says what a level permits.
 */
export const CAPABILITY_LEVEL_LABELS: Record<'read' | 'edit' | 'none' | 'unknown', string> = {
  edit: 'ویرایش',
  read: 'فقط‌خواندنی',
  none: 'بی‌مجوز',
  unknown: 'نخوانده‌شده',
};

export function capabilityLevelLabel(level: keyof typeof CAPABILITY_LEVEL_LABELS): string {
  return CAPABILITY_LEVEL_LABELS[level];
}

/** Any wire `job.status` string (contract types it loosely) → label. */
export function jobStatusLabel(status: string): string {
  return JOB_STATUS_LABELS[status as JobStatus] ?? status;
}

/** Any wire event `type` URI → Persian label (unknown kinds stay literal). */
export function eventKindLabel(type: string): string {
  const kind = eventKindOf(type);
  return kind ? EVENT_KIND_LABELS[kind] : type;
}

/** `io.wicore.acs.device.inform.v1` → `device.inform`. */
export function eventKindOf(type: string): EventTypeKind | null {
  const root = 'io.wicore.acs.';
  if (!type.startsWith(root) || !type.endsWith('.v1')) return null;
  const kind = type.slice(root.length, -'.v1'.length) as EventTypeKind;
  return Object.prototype.hasOwnProperty.call(EVENT_KIND_LABELS, kind) ? kind : null;
}

export function problemLabel(code: ProblemCode): string {
  return PROBLEM_LABELS[code];
}

/** Exhaustiveness guards used by the contract test. */
export function missingProblemLabels(): string[] {
  return PROBLEM_CODES.filter((code) => !PROBLEM_LABELS[code]);
}

export function missingActionLabels(): string[] {
  return ACTION_KINDS.filter((action) => !ACTION_LABELS[action]);
}

export function missingJobLabels(): string[] {
  return JOB_STATUSES.filter((status) => !JOB_STATUS_LABELS[status]);
}

export function missingActivationLabels(): string[] {
  return ACTIVATION_STATUSES.filter((status) => !ACTIVATION_STATUS_LABELS[status]);
}

export function missingStepLabels(): string[] {
  return ACTIVATION_STEP_NAMES.filter((step) => !ACTIVATION_STEP_LABELS[step]);
}

export function missingDiagnosticsTypeLabels(): string[] {
  return DIAGNOSTICS_TYPES.filter((type) => !DIAGNOSTICS_TYPE_LABELS[type]);
}

/** Guards for the alarms/OLT vocabularies (same rule: no silent enum hole). */
export function missingAlarmSeverityLabels(): string[] {
  return ALARM_SEVERITIES.filter((severity) => !ALARM_SEVERITY_LABELS[severity]);
}

export function missingOpticalWindowLabels(): string[] {
  return OPTICAL_WINDOWS.filter((window) => !OPTICAL_WINDOW_LABELS[window]);
}

/** Persian label for any wire step name; an unknown step stays literal (never dropped). */
export function activationStepLabel(step: string): string {
  return ACTIVATION_STEP_LABELS[step as ActivationStepName] ?? step;
}

/** Persian label for any wire diagnostics type; unknown types stay literal. */
export function diagnosticsTypeLabel(type: string): string {
  return DIAGNOSTICS_TYPE_LABELS[type as DiagnosticsType] ?? type;
}

/* ------------------------------------------------------------------ */
/* Campaigns — the wave ladder's own vocabulary (circuit E)             */
/* ------------------------------------------------------------------ */

/**
 * `Campaign.status`. `pending` is not «failed to start»: since E4 the create only
 * schedules, so the ladder is owed its first wave by the campaign worker.
 */
export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  pending: 'در انتظارِ اولین موج',
  running: 'در جریان',
  halted: 'متوقف‌شده',
  completed: 'تکمیل‌شده',
  rolling_back: 'در حال بازگشت به نسخهٔ قبل',
};

/**
 * `CampaignNextAction.action` — the six verdicts of `campaigns.py::plan`, which is the
 * answer to «why is my wave not moving?», so each word has to say what happens next.
 */
export const CAMPAIGN_ACTION_LABELS: Record<CampaignAction, string> = {
  advance: 'موج بعدی را باز می‌کند',
  continue_wave: 'همان موج را ادامه می‌دهد',
  wait_jobs: 'در انتظارِ تأیید دستگاه‌ها',
  wait_window: 'در انتظارِ پنجرهٔ زمانی',
  idle: 'نردبان به انتها رسیده',
  settled: 'بسته‌شده؛ چیزی dispatch نمی‌شود',
};

/** `CampaignWindow.status` — `passed` is a human decision, not another pass. */
export const CAMPAIGN_WINDOW_STATUS_LABELS: Record<CampaignWindowStatus, string> = {
  waiting: 'هنوز باز نشده',
  open: 'باز است',
  passed: 'گذشته است',
};

export const CAMPAIGN_KIND_LABELS: Record<CampaignKind, string> = {
  rollout: 'ارتقای موجدار',
  rollback: 'بازگشت تک‌-pass',
};

export function campaignStatusLabel(status: string): string {
  return CAMPAIGN_STATUS_LABELS[status as CampaignStatus] ?? status;
}

export function campaignActionLabel(action: string): string {
  return CAMPAIGN_ACTION_LABELS[action as CampaignAction] ?? action;
}

export function campaignWindowStatusLabel(status: string): string {
  return CAMPAIGN_WINDOW_STATUS_LABELS[status as CampaignWindowStatus] ?? status;
}

export function campaignKindLabel(kind: string): string {
  return CAMPAIGN_KIND_LABELS[kind as CampaignKind] ?? kind;
}

/** Exhaustiveness guards: a new server verdict cannot leave the console silent. */
export function missingCampaignStatusLabels(): string[] {
  return CAMPAIGN_STATUSES.filter((status) => !CAMPAIGN_STATUS_LABELS[status]);
}

export function missingCampaignActionLabels(): string[] {
  return CAMPAIGN_ACTIONS.filter((action) => !CAMPAIGN_ACTION_LABELS[action]);
}

export function missingCampaignWindowStatusLabels(): string[] {
  return CAMPAIGN_WINDOW_STATUSES.filter((status) => !CAMPAIGN_WINDOW_STATUS_LABELS[status]);
}

export function missingCampaignKindLabels(): string[] {
  return CAMPAIGN_KINDS.filter((kind) => !CAMPAIGN_KIND_LABELS[kind]);
}
